/**
 * Score Delta Indicator - Web Implementation
 *
 * Shows real-time score improvement as "+X points" badge with CSS transitions.
 * Part of Phase 2 UX Gamification for RLHF revision tracking.
 *
 * Design Decisions (Multi-Model Consensus 2025-12):
 * - Spring-like animation via CSS cubic-bezier (snappy, game-like feel)
 * - Integer precision (reduces cognitive load)
 * - Delta-only display (user cares about gain, not absolute math)
 * - Green glow effect (positive reinforcement)
 * - Respects prefers-reduced-motion
 *
 * @see REVISION_TRACKING_DATA_MODEL.md for architecture
 */

import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, AccessibilityInfo } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { useSDUIConfig, toUISpecTier } from '@dramadetector/ui/json-render';
import { useEntitlements } from '@/hooks/useEntitlements';
import { formatScoreDelta } from './formatScoreDelta';
import type { ScoreDeltaIndicatorProps } from './types';

// Animation timing constants (spring-like via CSS)
const ANIMATION_DURATION = 400; // ms
const SCALE_PEAK = 1.15; // Scale up then settle to 1.0

/**
 * ScoreDeltaIndicator - Web Implementation
 *
 * Displays score improvement with CSS spring-like animation.
 * Only renders when scoreDelta > 0.
 */
export function ScoreDeltaIndicator({
  scoreDelta,
  isVisible = true,
  animationDelay = 0,
  onAnimationComplete,
  testID = 'score-delta-indicator',
}: ScoreDeltaIndicatorProps) {
  const { t } = useTranslation('features');
  const { tier } = useEntitlements();
  // Tier from entitlements — enables future per-tier score display config
  const { config: scoreConfig } = useSDUIConfig('score-delta', toUISpecTier(tier));
  const [isAnimating, setIsAnimating] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const prevDeltaRef = useRef(scoreDelta);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check for reduced motion preference
  useEffect(() => {
    // Web: Check media query
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    return undefined;
  }, []);

  // Trigger animation when delta increases
  useEffect(() => {
    if (scoreDelta > prevDeltaRef.current && scoreDelta > 0) {
      // Clear any pending timeout (cancelable debounce per O3 recommendation)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Delay animation if specified
      timeoutRef.current = setTimeout(() => {
        setIsAnimating(true);

        // Reset animation after duration
        setTimeout(() => {
          setIsAnimating(false);
          onAnimationComplete?.();
        }, ANIMATION_DURATION);
      }, animationDelay);
    }

    prevDeltaRef.current = scoreDelta;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scoreDelta, animationDelay, onAnimationComplete]);

  // Don't render if no improvement or not visible
  if (!isVisible || scoreDelta <= 0) {
    return null;
  }

  // Determine animation style
  const animationStyle = prefersReducedMotion
    ? {} // No animation for reduced motion
    : isAnimating
      ? {
          transform: [{ scale: SCALE_PEAK }],
          opacity: 1,
        }
      : {
          transform: [{ scale: 1 }],
          opacity: 1,
        };

  const formattedDelta = formatScoreDelta(scoreDelta, scoreConfig, t);

  return (
    <View
      style={[styles.container, animationStyle]}
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={t('inline_annotations.score_delta_aria', {
        count: scoreDelta,
        defaultValue: `Score improved by ${scoreDelta} points`,
      })}
      // ARIA live region with polite priority (O3 recommendation)
      accessibilityLiveRegion="polite"
    >
      <Text style={styles.deltaText}>
        {formattedDelta}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'rgba(22, 163, 74, 0.15)', // Green tint
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignSelf: 'flex-start',
    // Green glow effect (box-shadow via web styles)
    boxShadow: '0 0 12px rgba(22, 163, 74, 0.4)',
    // Spring-like transition
    // @ts-expect-error - Web-only style
    transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity ${ANIMATION_DURATION}ms ease-out`,
  },
  deltaText: {
    color: '#16A34A', // Green-600
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
});

export default ScoreDeltaIndicator;
